import { EmailRequestFor, EmailTemplate } from './types';

import { STATIC_ASSETS } from '@/constants.js';

const actions = {
  fetchEmailTemplates(context, { id, requestFor }: { id: string; requestFor: EmailRequestFor }) {
    context.commit('setLoadingEmailTemplatesList', true);
    let url = '';
    if (requestFor === 'event') {
      url = `/event/email-templates/?event=${id}`;
    } else if (requestFor === 'event_template') {
      url = `/studio_template/studio_email_template/?event_template=${id}`;
    } else if (requestFor === 'series') {
      url = `/series/email-template/?series=${id}`;
    } else {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Failed to fetch email templates',
          mode: 'error'
        },
        {
          root: true
        }
      );
      return Promise.reject('No event or template ID provided');
    }

    return new Promise((resolve, reject) => {
      return context
        .dispatch(
          'makeapicall',
          {
            url: url,
            method: 'GET'
          },
          { root: true }
        )
        .then(emailTemplates => {
          context.commit('setEmailTemplates', emailTemplates);
          context.commit('setEmailTemplatesFor', id);
          resolve(emailTemplates);
        })
        .catch(error => {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Error while loading email templates.',
              mode: 'error'
            },
            {
              root: true
            }
          );
          reject(error);
        })
        .finally(() => {
          context.commit('setLoadingEmailTemplatesList', false);
        });
    });
  },
  fetchEmailTemplateByID(context, templateId: string) {
    context.commit('setLoadingTemplateCreateUpdate', true);
    return new Promise((resolve, reject) => {
      return context
        .dispatch(
          'makeapicall',
          {
            url: `/event/email-templates/${templateId}`,
            method: 'GET'
          },
          { root: true }
        )
        .then(emailTemplate => {
          context.commit('setEmailTemplate', emailTemplate);
          resolve(emailTemplate);
        })
        .catch(error => {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Template fetching failed.',
              mode: 'error'
            },
            {
              root: true
            }
          );
          reject(error);
        })
        .finally(() => {
          context.commit('setLoadingTemplateCreateUpdate', false);
        });
    });
  },
  createEmailTemplate(
    context,
    {
      emailTemplate,
      requestFor
    }: {
      emailTemplate: EmailTemplate;
      requestFor: EmailRequestFor;
    }
  ) {
    context.commit('setLoadingTemplateCreateUpdate', true);
    let url = '';
    if (requestFor === 'event') {
      url = `/event/email-templates/`;
    } else if (requestFor === 'event_template') {
      url = '/studio_template/studio_email_template/';
    } else if (requestFor === 'series') {
      url = '/series/email-template/';
    } else {
      return Promise.reject('No template context found');
    }
    return new Promise((resolve, reject) => {
      return context
        .dispatch(
          'makeapicall',
          {
            url: url,
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailTemplate)
          },
          { root: true }
        )
        .then(newEmailTemplate => {
          context.commit('addNewEmailTemplate', newEmailTemplate);
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Email Template successfully created',
              mode: 'success'
            },
            {
              root: true
            }
          );
          resolve(newEmailTemplate);
        })
        .catch(reject);
    });
  },
  async deleteEmailTemplate(
    context,
    {
      emailTemplateId,
      requestFor
    }: {
      emailTemplateId: string;
      requestFor: EmailRequestFor;
    }
  ) {
    context.commit('setDeleteTemplateLoader', true);

    let url = '';
    if (requestFor === 'event') {
      url = `/event/email-templates/${emailTemplateId}/`;
    } else if (requestFor === 'event_template') {
      url = `/studio_template/studio_email_template/${emailTemplateId}/`;
    } else if (requestFor === 'series') {
      url = `/series/email-template/${emailTemplateId}/`;
    } else {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: 'Error trying to delete this template',
          mode: 'success'
        },
        {
          root: true
        }
      );
      return Promise.reject('No template context found');
    }

    return new Promise((resolve, reject) => {
      return context
        .dispatch(
          'makeapicall',
          {
            url: url,
            method: 'delete'
          },
          { root: true }
        )
        .then(() => {
          context.commit('removeTemplate', emailTemplateId);
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Email Template successfully deleted',
              mode: 'success'
            },
            {
              root: true
            }
          );
          resolve(emailTemplateId);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setDeleteTemplateLoader', false);
        });
    });
  },
  async duplicateEmailTemplate(
    context,
    {
      emailTemplate,
      requestFor
    }: {
      emailTemplate: EmailTemplate;
      requestFor: EmailRequestFor;
    }
  ) {
    const postBody = {
      name: ('Copy of ' + emailTemplate.name).substring(0, 255),
      email_html: emailTemplate.email_html,
      email_json: emailTemplate.email_json
    };

    let url = '';
    if (requestFor === 'event') {
      url = `/event/email-templates/${emailTemplate.id}/duplicate/`;
    } else if (requestFor === 'event_template') {
      url = `/studio_template/studio_email_template/${emailTemplate.id}/duplicate/`;
    } else if (requestFor === 'series') {
      url = `/series/email-template/${emailTemplate.id}/duplicate/`;
    } else {
      return Promise.reject('No template context found');
    }

    return new Promise((resolve, reject) => {
      return context
        .dispatch(
          'makeapicall',
          {
            url: url,
            method: 'POST',
            body: JSON.stringify(postBody)
          },
          { root: true }
        )
        .then(newEmailTemplate => {
          context.commit('addNewEmailTemplate', newEmailTemplate);
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Email Template successfully duplicated',
              mode: 'success'
            },
            {
              root: true
            }
          );
          resolve(newEmailTemplate);
        })
        .catch(error => {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Email template duplication failed, Please try again',
              mode: 'error'
            },
            {
              root: true
            }
          );
          reject(error);
        });
    });
  },
  async updateEmailTemplate(
    context,
    {
      emailTemplate,
      requestFor
    }: {
      emailTemplate: EmailTemplate;
      requestFor: EmailRequestFor;
    }
  ) {
    context.commit('setLoadingTemplateCreateUpdate', true);
    let url = '';
    if (requestFor === 'event') {
      url = `/event/email-templates/${emailTemplate.id}/`;
    } else if (requestFor === 'event_template') {
      url = `/studio_template/studio_email_template/${emailTemplate.id}/`;
    } else if (requestFor === 'series') {
      url = `/series/email-template/${emailTemplate.id}/`;
    } else {
      return Promise.reject('No template context found');
    }
    return new Promise((resolve, reject) => {
      return context
        .dispatch(
          'makeapicall',
          {
            url: url,
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailTemplate)
          },
          { root: true }
        )
        .then(updatedEmailTemplate => {
          context.commit('setEmailTemplate', updatedEmailTemplate);
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Email Template successfully updated',
              mode: 'success'
            },
            {
              root: true
            }
          );
          resolve(updatedEmailTemplate);
        })
        .catch(reject);
    });
  },
  async fetchBlankEmailTemplateJson(context) {
    context.commit('setLoadingTemplateCreateUpdate', true);
    return new Promise((resolve, reject) => {
      return fetch(`${STATIC_ASSETS.CLOUDFRONT_URL}/${STATIC_ASSETS.ADMIN_PREFIX}/emptyTemplate.json`)
        .then(response => {
          return response.json();
        })
        .then(emailTemplateJson => {
          context.commit('setBlankEmailTemplateJson', JSON.stringify(emailTemplateJson));
          resolve(emailTemplateJson);
        })
        .catch(error => {
          context.dispatch(
            'studioToast/triggerToast',
            {
              msg: 'Error while loading email templates.',
              mode: 'error'
            },
            {
              root: true
            }
          );
          reject(error);
        })
        .finally(() => {
          context.commit('setLoadingTemplateCreateUpdate', false);
        });
    });
  }
};

export default actions;
